/**
 * Hook to load external demand data from GRM number
 * Used when user comes from redirect URL with grm_demand_number parameter
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

export const useExternalDemandLoad = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [demandData, setDemandData] = useState(null);

  // Get parameters from URL
  const grmDemandNumber = searchParams.get('grm_demand_number');
  const requestType = searchParams.get('request_type');
  const isExternal = !!(grmDemandNumber && requestType);

  useEffect(() => {
    if (!isExternal) {
      return;
    }

    const loadDemand = async () => {
      setLoading(true);
      setError(null);

      try {
        // Step 1: Check if this GRM number already exists in any request table.
        // This is the authoritative duplicate check — it queries all 5 tables.
        const dupCheck = await axios.get(`${config.apiUrl}/api/generic/check-grm-number/${grmDemandNumber}`);
        if (dupCheck.data?.exists && dupCheck.data?.request_type !== 'GRM Staging (pending)') {
          setError({
            message: `Demand number ${grmDemandNumber} has already been submitted as an ODM request (${dupCheck.data.request_type}). If you believe this is a mistake, please contact the ODM Portal team.`,
            code: 'ALREADY_SUBMITTED',
          });
          return;
        }

        // Step 2: Load pre-fill data from the staging table
        const response = await axios.get(`${config.apiUrl}/api/requests/by-grm/${grmDemandNumber}`);

        if (response.data) {
          // Validate that the request_type in the URL matches what is stored in the DB
          if (response.data.request_type !== requestType) {
            setError({
              message: `Request type mismatch: the URL specifies "${requestType}" but demand ${grmDemandNumber} is registered as "${response.data.request_type}". Please use the correct link from GRM Portal.`,
              code: 'TYPE_MISMATCH',
            });
          } else {
            setDemandData(response.data);
            console.log('Loaded external demand:', grmDemandNumber);
          }
        } else {
          setError({
            message: 'Demand not found',
            code: 'NOT_FOUND'
          });
        }
      } catch (err) {
        console.error('Error loading external demand:', err);
        const backendCode = err.response?.data?.code;
        const status = err.response?.status;
        setError({
          message: err.response?.data?.error || 'Failed to load demand data. Please try again.',
          code: backendCode || (status === 404 ? 'NOT_FOUND' : status === 409 ? 'ALREADY_SUBMITTED' : 'ERROR'),
        });
      } finally {
        setLoading(false);
      }
    };

    loadDemand();
  }, [grmDemandNumber, isExternal]);

  return {
    loading,
    error,
    demandData,
    isExternal,
    grmDemandNumber,
    requestType
  };
};

export default useExternalDemandLoad;
